import { motion } from "framer-motion";
import { Progress } from "@/components/ui/progress";
import { MapPin, ArrowRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

export const Campaigns = ({ campaigns, onDonate }) => {
  const { t } = useLang();
  return (
    <section id="campaigns" data-testid="campaigns-section" className="py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <p className="text-xs tracking-[0.3em] uppercase font-bold text-primary mb-5">{t("campaigns.overline")}</p>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight text-foreground leading-tight">
            {t("campaigns.title")}
          </h2>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {campaigns.map((c, i) => {
            const pct = Math.min(100, Math.round((c.raised / c.goal) * 100));
            return (
              <motion.article
                key={c.id}
                initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.7, delay: i * 0.12 }}
                data-testid={`campaign-card-${c.id}`}
                className="flex flex-col border border-border rounded-2xl overflow-hidden bg-card group"
              >
                <div className="relative h-60 overflow-hidden">
                  <img src={c.image} alt={c.title}
                       className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute top-4 left-4 rounded-full bg-background/90 backdrop-blur px-4 py-1.5 text-xs font-semibold text-foreground">
                    {c.cause}
                  </span>
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <h3 className="font-serif text-3xl text-foreground leading-tight">{c.title}</h3>
                  <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MapPin size={13} /> {c.location}
                  </p>
                  <p className="mt-4 text-sm text-muted-foreground leading-relaxed flex-1">{c.description}</p>

                  <div className="mt-6">
                    <Progress value={pct} className="h-2 bg-muted" />
                    <div className="mt-3 flex items-center justify-between text-sm">
                      <span className="font-bold text-primary">{inr(c.raised)}</span>
                      <span className="text-muted-foreground">{t("campaigns.of")} {inr(c.goal)} · {pct}%</span>
                    </div>
                  </div>

                  <button
                    data-testid={`donate-campaign-${c.id}`}
                    onClick={() => onDonate(c)}
                    className="mt-6 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm font-semibold inline-flex items-center justify-center gap-2 hover:scale-[1.02] transition-transform"
                  >
                    {t("campaigns.donate")} <ArrowRight size={16} />
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};
